import { getShopifyClient, PRODUCT_FRAGMENT, CART_FRAGMENT } from './shopify';

export interface ShopifyProduct {
  id: string;
  handle: string;
  title: string;
  description: string;
  tags: string[];
  vendor: string;
  productType: string;
  availableForSale: boolean;
  priceRange: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
    maxVariantPrice: {
      amount: string;
      currencyCode: string;
    };
  };
  compareAtPriceRange?: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
    maxVariantPrice: {
      amount: string;
      currencyCode: string;
    };
  };
  featuredImage: {
    url: string;
    altText: string;
    width: number;
    height: number;
  };
  images: {
    edges: Array<{
      node: {
        url: string;
        altText: string;
        width: number;
        height: number;
      };
    }>;
  };
  variants: {
    edges: Array<{
      node: {
        id: string;
        title: string;
        availableForSale: boolean;
        quantityAvailable: number;
        price: {
          amount: string;
          currencyCode: string;
        };
        compareAtPrice?: {
          amount: string;
          currencyCode: string;
        };
        selectedOptions: Array<{
          name: string;
          value: string;
        }>;
      };
    }>;
  };
}

export interface ShopifyCart {
  id: string;
  checkoutUrl: string;
  totalQuantity: number;
  cost: {
    totalAmount: {
      amount: string;
      currencyCode: string;
    };
    subtotalAmount: {
      amount: string;
      currencyCode: string;
    };
    totalTaxAmount?: {
      amount: string;
      currencyCode: string;
    };
  };
  lines: {
    edges: Array<{
      node: {
        id: string;
        quantity: number;
        cost: {
          totalAmount: {
            amount: string;
            currencyCode: string;
          };
        };
        merchandise: {
          id: string;
          title: string;
          price: {
            amount: string;
            currencyCode: string;
          };
          product: {
            title: string;
            handle: string;
            featuredImage: {
              url: string;
              altText: string;
              width: number;
              height: number;
            };
          };
        };
      };
    }>;
  };
}

// Get all products
export async function getProducts(): Promise<ShopifyProduct[]> {
  const query = `
    query getProducts($first: Int!) {
      products(first: $first) {
        edges {
          node {
            ...ProductFragment
          }
        }
      }
    }
    ${PRODUCT_FRAGMENT}
  `;

  try {
    const client = getShopifyClient();
    const { data } = await client.request(query, { variables: { first: 100 } });
    return data.products.edges.map((edge: any) => edge.node);
  } catch (error) {
    console.error('Error fetching products:', error);
    return [];
  }
}

// Get a single product by handle
export async function getProduct(handle: string): Promise<ShopifyProduct | null> {
  const query = `
    query getProduct($handle: String!) {
      product(handle: $handle) {
        ...ProductFragment
      }
    }
    ${PRODUCT_FRAGMENT}
  `;

  try {
    const client = getShopifyClient();
    const { data } = await client.request(query, { variables: { handle } });
    return data.product;
  } catch (error) {
    console.error('Error fetching product:', error);
    return null;
  }
}

// Create a new cart
export async function createCart(): Promise<string> {
  const query = `
    mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          id
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  try {
    const client = getShopifyClient();
    const { data } = await client.request(query, {
      variables: {
        input: {}
      }
    });

    if (data.cartCreate.userErrors.length > 0) {
      throw new Error(data.cartCreate.userErrors[0].message);
    }

    return data.cartCreate.cart.id;
  } catch (error) {
    console.error('Error creating cart:', error);
    throw error;
  }
}

// Get cart by ID
export async function getCart(cartId: string): Promise<ShopifyCart | null> {
  const query = `
    query getCart($id: ID!) {
      cart(id: $id) {
        ...CartFragment
      }
    }
    ${CART_FRAGMENT}
  `;

  try {
    const client = getShopifyClient();
    const { data } = await client.request(query, { variables: { id: cartId } });
    return data.cart;
  } catch (error) {
    console.error('Error fetching cart:', error);
    return null;
  }
}

// Add items to cart
export async function addToCart(cartId: string, variantId: string, quantity: number = 1): Promise<ShopifyCart | null> {
  const query = `
    mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart {
          ...CartFragment
        }
        userErrors {
          field
          message
        }
      }
    }
    ${CART_FRAGMENT}
  `;

  try {
    const client = getShopifyClient();
    const { data } = await client.request(query, {
      variables: {
        cartId,
        lines: [
          {
            merchandiseId: variantId,
            quantity
          }
        ]
      }
    });


    if (data.cartLinesAdd.userErrors.length > 0) {
      console.error('Shopify user errors:', data.cartLinesAdd.userErrors);
      throw new Error(data.cartLinesAdd.userErrors[0].message);
    }

    return data.cartLinesAdd.cart;
  } catch (error) {
    console.error('Error adding to cart:', error);
    return null;
  }
}

// Update cart line quantities
export async function updateCartLines(cartId: string, lines: Array<{ id: string; quantity: number }>): Promise<ShopifyCart | null> {
  const query = `
    mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart {
          ...CartFragment
        }
        userErrors {
          field
          message
        }
      }
    }
    ${CART_FRAGMENT}
  `;

  try {
    const client = getShopifyClient();
    const { data } = await client.request(query, {
      variables: { cartId, lines }
    });

    if (data.cartLinesUpdate.userErrors.length > 0) {
      throw new Error(data.cartLinesUpdate.userErrors[0].message);
    }

    return data.cartLinesUpdate.cart;
  } catch (error) {
    console.error('Error updating cart:', error);
    return null;
  }
}

// Remove items from cart
export async function removeFromCart(cartId: string, lineIds: string[]): Promise<ShopifyCart | null> {
  const query = `
    mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart {
          ...CartFragment
        }
        userErrors {
          field
          message
        }
      }
    }
    ${CART_FRAGMENT}
  `;

  try {
    const client = getShopifyClient();
    const { data } = await client.request(query, {
      variables: { cartId, lineIds }
    });

    if (data.cartLinesRemove.userErrors.length > 0) {
      throw new Error(data.cartLinesRemove.userErrors[0].message);
    }

    return data.cartLinesRemove.cart;
  } catch (error) {
    console.error('Error removing from cart:', error);
    return null;
  }
}

// Clear all discount codes from cart
export async function clearDiscountsFromCart(cartId: string): Promise<ShopifyCart | null> {
  const query = `
    mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
      cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
        cart {
          ...CartFragment
        }
        userErrors {
          field
          message
        }
      }
    }
    ${CART_FRAGMENT}
  `;

  try {
    const client = getShopifyClient();
    const { data } = await client.request(query, {
      variables: { cartId, discountCodes: [] } // Empty array clears all discounts
    });

    if (data.cartDiscountCodesUpdate.userErrors.length > 0) {
      console.error('Discount clearing errors:', data.cartDiscountCodesUpdate.userErrors);
      throw new Error(data.cartDiscountCodesUpdate.userErrors[0].message);
    }

    return data.cartDiscountCodesUpdate.cart;
  } catch (error) {
    console.error('Error clearing discounts from cart:', error);
    return null;
  }
}

// Apply discount codes to cart
export async function applyDiscountToCart(cartId: string, discountCodes: string[]): Promise<ShopifyCart | null> {
  const query = `
    mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
      cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
        cart {
          ...CartFragment
        }
        userErrors {
          field
          message
        }
      }
    }
    ${CART_FRAGMENT}
  `;

  try {
    const client = getShopifyClient();
    const { data } = await client.request(query, {
      variables: { cartId, discountCodes }
    });

    if (data.cartDiscountCodesUpdate.userErrors.length > 0) {
      console.error('Discount application errors:', data.cartDiscountCodesUpdate.userErrors);
      throw new Error(data.cartDiscountCodesUpdate.userErrors[0].message);
    }

    return data.cartDiscountCodesUpdate.cart;
  } catch (error) {
    console.error('Error applying discount to cart:', error);
    return null;
  }
}